import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useInitiatives } from "@/lib/useInitiatives";

const linkCls = ({ isActive }) =>
  `px-3 h-[72px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
    isActive ? "text-brand border-teal" : "text-ink/80 border-transparent hover:text-brand"
  }`;

export default function Navbar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [initOpen, setInitOpen] = useState(false);
  const { byCategory } = useInitiatives();

  const groups = [
    { key: "centre", label: t("nav.centres"), items: byCategory.centre },
    { key: "programme", label: t("nav.programmes"), items: byCategory.programme },
    { key: "incubation", label: t("nav.incubation"), items: byCategory.incubation },
  ];

  const mission = byCategory.mission?.[0];

  return (
    <header
      data-testid="site-header"
      role="banner"
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-ink/10 shadow-[0_1px_0_rgba(0,72,136,0.06)]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between gap-6">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 shrink-0" aria-label={t("meta.brand")}>
          <img src="/semicon-keralam-logo.png" alt={`${t("meta.brand")} — ${t("meta.tagline")}`} className="h-12 w-auto" />
          <div className="hidden xl:block leading-tight border-l border-ink/15 pl-3">
            <div className="font-mono text-[10px] tracking-widest text-brand uppercase">{t("meta.tagline")}</div>
            <div className="font-mono text-[9px] tracking-widest text-ink/60 uppercase">{t("meta.agency")}</div>
          </div>
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex items-center">
          <NavLink to="/" end data-testid="nav-home" className={linkCls}>{t("nav.home")}</NavLink>
          {mission && (
            <NavLink to={`/initiatives/${mission.slug}`} data-testid="nav-mission" className={linkCls}>{t("nav.mission")}</NavLink>
          )}
          <div className="relative" onMouseEnter={() => setInitOpen(true)} onMouseLeave={() => setInitOpen(false)}>
            <button
              data-testid="nav-initiatives"
              aria-haspopup="menu"
              aria-expanded={initOpen}
              onClick={() => setInitOpen((o) => !o)}
              className={`px-3 h-[72px] flex items-center gap-1 whitespace-nowrap text-sm font-medium border-b-2 border-transparent ${initOpen ? "text-brand" : "text-ink/80 hover:text-brand"}`}
            >
              {t("nav.initiatives")} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${initOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {initOpen && (
              <div role="menu" className="absolute left-1/2 -translate-x-1/2 top-full w-[880px] bg-white border border-ink/10 shadow-2xl p-6 grid grid-cols-3 gap-6">
                {groups.map((g) => (
                  <div key={g.key}>
                    <div className="tag mb-3">{g.label}</div>
                    <ul className="space-y-0.5">
                      {g.items.map((c) => (
                        <li key={c.slug}>
                          <NavLink
                            to={`/initiatives/${c.slug}`}
                            role="menuitem"
                            data-testid={`nav-init-${c.slug}`}
                            onClick={() => setInitOpen(false)}
                            className={({ isActive }) =>
                              `block px-2 py-1.5 text-[13px] leading-snug rounded-sm ${isActive ? "text-brand bg-surface" : "text-ink/80"} hover:text-brand hover:bg-surface`
                            }
                          >
                            {c.title}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <Link
                  to="/initiatives"
                  data-testid="nav-init-all"
                  onClick={() => setInitOpen(false)}
                  className="col-span-3 mt-2 pt-4 border-t border-ink/10 inline-flex items-center gap-2 text-sm font-medium text-teal hover:text-teal-dark"
                >
                  {t("nav.all_initiatives")} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </div>
            )}
          </div>
          <NavLink to="/chipmat" data-testid="nav-chipmat" className={linkCls}>{t("nav.chipmat")}</NavLink>
          <NavLink to="/incubate" data-testid="nav-incubate" className={linkCls}>{t("nav.incubate")}</NavLink>
          <NavLink to="/startups" data-testid="nav-startups" className={linkCls}>{t("nav.startups")}</NavLink>
          <NavLink to="/directors" data-testid="nav-board" className={linkCls}>{t("nav.board")}</NavLink>
          <NavLink to="/careers" data-testid="nav-careers" className={linkCls}>{t("nav.careers")}</NavLink>
          <NavLink to="/tenders" data-testid="nav-tenders" className={linkCls}>{t("nav.tenders")}</NavLink>
          <NavLink to="/gallery" data-testid="nav-gallery" className={linkCls}>{t("nav.gallery")}</NavLink>
          <NavLink to="/contact" data-testid="nav-contact" className={linkCls}>{t("nav.contact")}</NavLink>
        </nav>

        <div className="hidden lg:block"><LanguageSwitcher /></div>

        <button
          data-testid="mobile-menu-btn"
          aria-label={t("nav.menu")}
          aria-expanded={open}
          aria-controls="mobile-nav"
          className="lg:hidden text-brand p-2"
          onClick={() => setOpen(!open)}
        >
          {open ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
        </button>
      </div>

      {open && (
        <div id="mobile-nav" className="lg:hidden border-t border-ink/10 bg-white max-h-[calc(100vh-72px)] overflow-y-auto">
          <div className="px-6 py-4 border-b border-ink/5 flex items-center justify-between">
            <span className="tag">{t("a11y.language")}</span>
            <LanguageSwitcher compact />
          </div>
          {[
            ["/", "home", t("nav.home")],
            mission && [`/initiatives/${mission.slug}`, "mission", t("nav.mission")],
            ["/chipmat", "chipmat", t("nav.chipmat")],
            ["/incubate", "incubate", t("nav.incubate")],
            ["/startups", "startups", t("nav.startups")],
            ["/directors", "board", t("nav.board")],
            ["/careers", "careers", t("nav.careers")],
            ["/tenders", "tenders", t("nav.tenders")],
            ["/gallery", "gallery", t("nav.gallery")],
            ["/contact", "contact", t("nav.contact")],
          ].filter(Boolean).map(([to, key, label]) => (
            <NavLink
              key={key}
              to={to}
              data-testid={`mnav-${key}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `block px-6 py-3 text-sm border-b border-ink/5 ${isActive ? "text-brand font-medium" : "text-ink/80"}`}
            >
              {label}
            </NavLink>
          ))}
          {groups.map((g) => (
            <div key={g.key} className="border-b border-ink/5">
              <div className="px-6 pt-4 pb-2 tag">{g.label}</div>
              {g.items.map((c) => (
                <NavLink
                  key={c.slug}
                  to={`/initiatives/${c.slug}`}
                  data-testid={`mnav-init-${c.slug}`}
                  onClick={() => setOpen(false)}
                  className="block px-8 py-2 text-sm text-ink/80 hover:text-brand"
                >
                  {c.title}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      )}
    </header>
  );
}
